import { Button } from '@/components/button';
import SimpleDialog from '@/components/dialog/simple-dialog';
import { cn } from '@/lib/utils';
import {
  useResetPropfirmOfferOverrideMutation,
  useSavePropfirmOfferOverrideMutation,
} from '@/service/propfirm-offer/propfirm-offer.hook';
import type { OfferFigures } from '@/service/propfirm-offer/propfirm-offer-types';
import { useGetMeQuery } from '@/service/user/user.hook';
import { UserRole } from '@/types/database-types';
import {
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MdCheck,
  MdClose,
  MdEdit,
  MdOutlineGavel,
  MdOutlineSmartToy,
  MdOutlineTableChart,
} from 'react-icons/md';

import { ALGO_USES } from '../data/algo-trading';
import { offerCorrection } from '../data/catalogue';
import { PROPFIRMS } from '../data/propfirms';
import type { DrawdownType, Propfirm, PropfirmAccount } from '../data/types';
import { useDataText } from '../data/use-data-text';
import { AlgoLevelValue, AlgoTradingBadge } from './algo-trading-badge';
import { ModelChips } from './comparator-cells';
import { formatCompactUsd, formatPrice, ptDdRatio } from './comparator-utils';
import { BoolValue, DetailCard, DetailSection, SourcesFooter } from './detail-parts';

const ACCT_GRID =
  '1.1fr 0.8fr 0.9fr 0.7fr 0.7fr 0.9fr 0.9fr 1fr 0.7fr 0.9fr 0.8fr';
const ACCT_MIN_WIDTH = 780;
/// Colonne du bouton de correction, pour les admins.
const ADMIN_COL = ' 32px';

const DRAWDOWNS: DrawdownType[] = ['static', 'trailing', 'eod-trailing'];
const FIGURES = [
  'priceUsd',
  'targetP1Pct',
  'targetP2Pct',
  'maxDailyLossPct',
  'maxDrawdownPct',
  'drawdownType',
] as const satisfies readonly (keyof OfferFigures)[];
const PCT_FIELDS = [
  { key: 'targetP1Pct', label: 'detail.override.targetP1' },
  { key: 'targetP2Pct', label: 'detail.override.targetP2' },
  { key: 'maxDailyLossPct', label: 'detail.override.dailyLoss' },
  { key: 'maxDrawdownPct', label: 'detail.override.maxDd' },
] as const;

const DD_CLASSES: Record<DrawdownType, string> = {
  static: 'bg-sky-500/15 text-sky-300',
  trailing: 'bg-amber-500/15 text-amber-300',
  'eod-trailing': 'bg-violet-500/15 text-violet-300',
};
const DD_KEYS = {
  static: 'detail.dd.static',
  trailing: 'detail.dd.trailing',
  'eod-trailing': 'detail.dd.eod',
} as const satisfies Record<DrawdownType, string>;

function DrawdownChip({ type }: { type: DrawdownType | null }) {
  const { t } = useTranslation('propfirms');
  if (!type) return <span className="text-neutral-600">{t('value.none')}</span>;
  return (
    <span
      className={cn(
        'rounded px-1.5 py-0.5 text-[11px] font-medium md:text-[10px]',
        DD_CLASSES[type],
      )}
    >
      {t(DD_KEYS[type])}
    </span>
  );
}

function Pct({ value }: { value: number | null }) {
  const { t } = useTranslation('propfirms');
  return <>{value != null ? `${value} %` : t('value.none')}</>;
}

/// Correction d'une offre par un admin : les chiffres changés remplacent ceux
/// du catalogue embarqué pour tous les utilisateurs.
function OfferOverrideDialog({
  firm,
  offer,
  onClose,
}: {
  firm: Propfirm;
  offer: PropfirmAccount;
  onClose: () => void;
}) {
  const { t } = useTranslation('propfirms');
  const dataText = useDataText();
  const save = useSavePropfirmOfferOverrideMutation();
  const reset = useResetPropfirmOfferOverrideMutation();
  const key = { firmSlug: firm.slug, label: offer.label };
  const corrected = offerCorrection(firm.slug, offer.label);
  const original = PROPFIRMS.find((f) => f.slug === firm.slug)?.accounts.find(
    (a) => a.label === offer.label,
  );
  const [draft, setDraft] = useState<OfferFigures>(() => ({
    priceUsd: offer.priceUsd,
    targetP1Pct: offer.targetP1Pct,
    targetP2Pct: offer.targetP2Pct,
    maxDailyLossPct: offer.maxDailyLossPct,
    maxDrawdownPct: offer.maxDrawdownPct,
    drawdownType: offer.drawdownType,
  }));

  // Seuls les chiffres qui diffèrent du catalogue embarqué sont corrigés : les
  // autres en suivront les mises à jour.
  const submit = () => {
    const changed = Object.fromEntries(
      FIGURES.filter((f) => draft[f] !== original?.[f]).map((f) => [
        f,
        draft[f],
      ]),
    ) as Partial<OfferFigures>;
    if (Object.keys(changed).length)
      save.mutate({ ...key, ...changed }, { onSuccess: onClose });
    else if (corrected) reset.mutate(key, { onSuccess: onClose });
    else onClose();
  };

  return (
    <SimpleDialog open onClose={onClose} title={t('detail.override.title')}>
      <div className="flex w-full flex-col gap-3 pt-1">
        <p className="text-[12px] text-neutral-400">
          {firm.name} · {dataText(offer.label)}
        </p>
        <TextField
          label={t('detail.override.price')}
          type="number"
          value={draft.priceUsd}
          inputProps={{ min: 0, step: 1 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {firm.priceCurrency ?? 'USD'}
              </InputAdornment>
            ),
          }}
          onChange={(e) =>
            setDraft((d) => ({
              ...d,
              priceUsd: Math.max(0, Number(e.target.value)),
            }))
          }
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {PCT_FIELDS.map(({ key, label }) => (
            <TextField
              key={key}
              label={t(label)}
              type="number"
              value={draft[key] ?? ''}
              inputProps={{ min: 0, max: 100, step: 0.5 }}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  [key]:
                    e.target.value === ''
                      ? null
                      : Math.min(100, Math.max(0, Number(e.target.value))),
                }))
              }
            />
          ))}
        </div>
        <TextField
          select
          label={t('detail.override.ddType')}
          value={draft.drawdownType ?? ''}
          onChange={(e) =>
            setDraft((d) => ({
              ...d,
              drawdownType: (e.target.value || null) as DrawdownType | null,
            }))
          }
        >
          <MenuItem value="">{t('detail.override.none')}</MenuItem>
          {DRAWDOWNS.map((d) => (
            <MenuItem key={d} value={d}>
              {t(DD_KEYS[d])}
            </MenuItem>
          ))}
        </TextField>
        <p className="text-[11px] text-neutral-500">
          {t('detail.override.hint')}
        </p>
        <div className="flex flex-wrap justify-end gap-2">
          {corrected && (
            <Button
              variant="ghost"
              className="mr-auto"
              loading={reset.isPending}
              onClick={() => reset.mutate(key, { onSuccess: onClose })}
            >
              {t('detail.override.reset')}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            {t('detail.override.cancel')}
          </Button>
          <Button loading={save.isPending} onClick={submit}>
            {t('detail.override.save')}
          </Button>
        </div>
      </div>
    </SimpleDialog>
  );
}

function AccountsMatrix({ firm }: { firm: Propfirm }) {
  const { t } = useTranslation('propfirms');
  const dataText = useDataText();
  const { data: me } = useGetMeQuery();
  const isAdmin = !!me?.roles.includes(UserRole.admin);
  const [editing, setEditing] = useState<PropfirmAccount | null>(null);
  const grid = isAdmin ? ACCT_GRID + ADMIN_COL : ACCT_GRID;
  return (
    <div className="min-w-0 max-w-full scroll-x-hint overflow-x-auto rounded-lg border border-white/[0.05] bg-[#0d0e12]">
      <div style={{ minWidth: ACCT_MIN_WIDTH }}>
        <div
          className="grid items-center border-b border-white/10 bg-[#16161a] px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-neutral-500 md:text-[10px]"
          style={{ gridTemplateColumns: grid }}
        >
          <span className="text-left">{t('detail.cols.size')}</span>
          <span>{t('detail.cols.model')}</span>
          <span>{t('detail.cols.price')}</span>
          <span>{t('detail.cols.targetP1')}</span>
          <span>{t('detail.cols.targetP2')}</span>
          <span>{t('detail.cols.dailyLoss')}</span>
          <span>{t('detail.cols.maxDd')}</span>
          <span>{t('detail.cols.ddType')}</span>
          <span>{t('detail.cols.split')}</span>
          <span>{t('detail.cols.ptdd')}</span>
          <span>{t('detail.cols.refundable')}</span>
          {isAdmin && <span />}
        </div>
        {firm.accounts.map((a, i) => (
          <div
            key={a.label}
            className={cn(
              'grid items-center border-b border-white/[0.04] px-2 py-2 text-center text-[11px] tabular-nums text-neutral-200 last:border-b-0',
              i % 2 === 1 && 'bg-white/[0.015]',
            )}
            style={{ gridTemplateColumns: grid }}
          >
            <span
              className="flex items-center gap-1.5 text-left font-medium text-neutral-100"
              title={dataText(a.label)}
            >
              {formatCompactUsd(a.sizeUsd)}
              {offerCorrection(firm.slug, a.label) && (
                <Tooltip title={t('detail.override.corrected')}>
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary-400" />
                </Tooltip>
              )}
            </span>
            <span className="flex justify-center">
              <ModelChips models={[a.model]} />
            </span>
            <span className="text-primary-400">{formatPrice(a.priceUsd, firm.priceCurrency)}</span>
            <span>
              <Pct value={a.targetP1Pct} />
            </span>
            <span>
              <Pct value={a.targetP2Pct} />
            </span>
            <span>
              <Pct value={a.maxDailyLossPct} />
            </span>
            <span>
              <Pct value={a.maxDrawdownPct} />
            </span>
            <span className="flex justify-center">
              <DrawdownChip type={a.drawdownType} />
            </span>
            <span>
              <Pct value={a.profitSplitPct} />
            </span>
            <span>{ptDdRatio(a) ?? t('value.none')}</span>
            <span className="flex justify-center">
              {a.refundableFee == null ? (
                <span className="text-neutral-600">{t('value.none')}</span>
              ) : a.refundableFee ? (
                <MdCheck size={15} className="text-green-400" />
              ) : (
                <MdClose size={15} className="text-neutral-500" />
              )}
            </span>
            {isAdmin && (
              <Tooltip title={t('detail.override.edit')}>
                <IconButton size="small" onClick={() => setEditing(a)}>
                  <MdEdit size={14} />
                </IconButton>
              </Tooltip>
            )}
          </div>
        ))}
      </div>
      {editing && (
        <OfferOverrideDialog
          firm={firm}
          offer={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function RulesGrid({ firm }: { firm: Propfirm }) {
  const { t } = useTranslation('propfirms');
  const dataText = useDataText();
  const { rules } = firm;
  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
      <DetailCard label={t('detail.rule.news')}>
        <BoolValue value={rules.newsTrading} />
      </DetailCard>
      <DetailCard label={t('detail.rule.weekend')}>
        <BoolValue value={rules.weekendHolding} />
      </DetailCard>
      <DetailCard label={t('detail.rule.ea')}>
        <span className="inline-flex items-center gap-1.5">
          <AlgoLevelValue level={rules.algo.automated} />
          <AlgoTradingBadge firm={firm} />
        </span>
      </DetailCard>
      <DetailCard label={t('detail.rule.copy')}>
        <AlgoLevelValue level={rules.algo.copyOwnAccounts} />
      </DetailCard>
      <DetailCard label={t('detail.rule.scaling')}>
        <BoolValue value={rules.scalingPlan} />
      </DetailCard>
      <DetailCard label={t('detail.rule.minDays')}>
        {rules.minTradingDays != null
          ? t('detail.daysShort', { count: rules.minTradingDays })
          : t('value.none')}
      </DetailCard>
      <DetailCard label={t('detail.rule.timeLimit')}>
        {rules.timeLimitDays != null
          ? t('detail.daysShort', { count: rules.timeLimitDays })
          : t('detail.unlimited')}
      </DetailCard>
      <DetailCard label={t('detail.rule.consistency')} wide>
        {rules.consistencyRule
          ? dataText(rules.consistencyRule)
          : t('detail.noConsistency')}
      </DetailCard>
    </div>
  );
}

/// Le trading automatisé, usage par usage : ce que la firme laisse faire au
/// terminal, au copieur et à une stratégie autonome, puis les conditions à
/// connaître et les pages d'où elles viennent. Trois réponses et non une,
/// parce qu'une même firme admet souvent l'un et refuse l'autre.
function AlgoPolicy({ firm }: { firm: Propfirm }) {
  const { t } = useTranslation('propfirms');
  const dataText = useDataText();
  const { algo } = firm.rules;
  return (
    <div className="flex flex-col gap-2.5">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
        {ALGO_USES.map((use) => (
          <DetailCard key={use} label={t(`algo.use.${use}`)}>
            <span className="flex flex-col gap-0.5">
              <AlgoLevelValue level={algo[use]} />
              <span className="text-[11px] leading-snug text-neutral-500 md:text-[10px]">
                {t(`algo.useHint.${use}`)}
              </span>
            </span>
          </DetailCard>
        ))}
      </div>
      <ul className="flex list-disc flex-col gap-1 pl-4 text-[11px] leading-relaxed text-neutral-400">
        {algo.notes.map((note) => (
          <li key={note}>{dataText(note)}</li>
        ))}
      </ul>
      <SourcesFooter notes="" sources={algo.sources} lastVerified={algo.verifiedAt} />
    </div>
  );
}

export default function PropfirmDetail({ firm }: { firm: Propfirm }) {
  const { t } = useTranslation('propfirms');
  const dataText = useDataText();
  return (
    <div className="flex flex-col gap-6">
      <DetailSection title={t('detail.accounts')} icon={<MdOutlineTableChart size={14} />}>
        <AccountsMatrix firm={firm} />
      </DetailSection>
      <DetailSection title={t('detail.rules')} icon={<MdOutlineGavel size={14} />}>
        <RulesGrid firm={firm} />
      </DetailSection>
      <DetailSection title={t('algo.section')} icon={<MdOutlineSmartToy size={14} />}>
        <AlgoPolicy firm={firm} />
      </DetailSection>
      <SourcesFooter
        notes={dataText(firm.notes)}
        sources={firm.sources}
        lastVerified={firm.lastVerified}
      />
    </div>
  );
}
